import React, { memo, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import urls from 'constants/urls';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/ingredients/TextLink';
import { Tooltip } from '@johnlewispartnership/wtr-ingredients/ingredients/Tooltip';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { IconButton } from '@johnlewispartnership/wtr-ingredients/core/IconButton';
import { Badge } from '@johnlewispartnership/wtr-ingredients/ingredients/Badge';
import { BadgeType } from '@johnlewispartnership/wtr-ingredients/ingredients/Badge/types';
import { ChevronRight, Close } from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import {
  trackShopByListCtaClick,
  trackShopByListCtaImpression,
  trackShopByListTooltipDismissal,
  trackShopByListTooltipImpression,
} from 'analytics/shop-by-list-tracking';
import { useIntersectionObserver } from 'hooks/use-intersection-observer';
import { useOneTimeTooltip } from 'hooks/use-one-time-tooltip';
import { useOnResize } from 'hooks/use-on-resize';
import { useClientSideMediaQuery } from 'hooks/use-client-side-media-query';
import { useScrolled } from 'hooks/use-scrolled';
import { stickyHeaderOffset } from 'constants/site-header';
import { BREAKPOINTS } from 'constants/grid';
import {
  SHOP_BY_LIST_CTA_TEXT,
  SHOP_BY_LIST_CTA_TOOLTIP_KEY,
} from 'components/ShopByListCta/constants';

import styles from './ShopByListCta.scss';

const TOOLTIP_PAGES = [urls.homepage, urls.groceriesHome];
const TOOLTIP_SKIDDING_DISTANCE = 30;

export interface ShopByListCtaProps {
  hidden?: boolean;
  onClick?: () => void;
  searchbox?: boolean;
}

let lastTrackedPathname: string | null = null;

const ShopByListCta = ({ hidden = false, onClick, searchbox }: ShopByListCtaProps) => {
  const [ctaElement, setCtaElement] = useState<HTMLAnchorElement | null>(null);
  const { isIntersecting } = useIntersectionObserver(ctaElement);
  const { pathname } = useLocation();

  const ctaRef = useCallback((element: HTMLAnchorElement | null) => {
    setCtaElement(element);
  }, []);

  const { isTooltipOpen, closeTooltip, closeTooltipTemporarily } = useOneTimeTooltip(
    SHOP_BY_LIST_CTA_TOOLTIP_KEY,
    {
      storeStateOn: 'close',
      minVersion: 1,
    },
  );

  useOnResize(isTooltipOpen ? closeTooltipTemporarily : undefined);

  const isMobile = useClientSideMediaQuery({ maxWidth: BREAKPOINTS.md });
  const { scrolled: isStickyHeaderTriggered } = useScrolled(stickyHeaderOffset);

  const isOnShopByListPage = pathname.startsWith(urls.shopByList);
  const isOnTooltipPage = TOOLTIP_PAGES.includes(pathname);
  const distanceOffset = searchbox ? -2 : 4;
  const placement = searchbox && isMobile ? 'bottom' : 'bottom-start';
  const showTooltip = isTooltipOpen && isOnTooltipPage && !isStickyHeaderTriggered;

  const handleClick = useCallback(() => {
    trackShopByListCtaClick();
    closeTooltip();

    onClick?.();
  }, [closeTooltip, onClick]);

  const handleTooltipClose = useCallback(() => {
    trackShopByListTooltipDismissal();
    closeTooltip();
  }, [closeTooltip]);

  useEffect(() => {
    if (hidden || !isIntersecting || lastTrackedPathname === pathname) {
      return;
    }

    lastTrackedPathname = pathname;

    trackShopByListCtaImpression();
  }, [hidden, isIntersecting, pathname]);

  useEffect(() => {
    if (showTooltip) {
      trackShopByListTooltipImpression();
    }
  }, [showTooltip]);

  if (hidden) {
    return null;
  }

  return (
    <>
      <Tooltip
        className={styles.tooltip}
        content={
          <Typography styleAs="paragraphSmall" textStyle="body-s" noMargins>
            Upload a photo or paste your shopping list. We&apos;ll match your items so you can add
            them straight to your trolley.
            <IconButton
              className={styles.tooltipClose}
              onClick={handleTooltipClose}
              aria-label="Close tooltip"
            >
              <Close size="xsmall" />
            </IconButton>
          </Typography>
        }
        skiddingOffset={TOOLTIP_SKIDDING_DISTANCE}
        distanceOffset={distanceOffset}
        placement={placement}
        isOpen={showTooltip}
        disableFocusListener
        disableHoverListener
        disableTouchListener
      >
        {/* Without the span, the tooltip will appear on the top of the page */}
        <span
          className={classNames({
            [styles.searchboxCtaWrapper]: searchbox,
          })}
        >
          <TextLink
            className={classNames(styles.cta, {
              [styles.searchboxCta]: searchbox,
              [styles.fade]: isOnShopByListPage,
            })}
            component={Link}
            onClick={handleClick}
            ref={ctaRef}
            to={urls.shopByList}
          >
            {SHOP_BY_LIST_CTA_TEXT}
          </TextLink>
        </span>
      </Tooltip>
      <Badge
        className={classNames(styles.badge, {
          [styles.searchboxBadge]: searchbox,
        })}
        text="New"
        textStyle="paragraphExtraSmall"
        type={isOnShopByListPage ? BadgeType.NEUTRAL : BadgeType.GREEN}
      />
      {!searchbox && <ChevronRight size="xsmall" className={styles.chevronRightIcon} />}
    </>
  );
};

export default memo(ShopByListCta);
